import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Checkbox,
    CollapsibleSection,
    Colors,
    Icon,
    ModalDialog,
    SearchBar,
    Text,
} from '@amelio/ui-library';
import { useEffect, useMemo, useState } from 'react';

import type { CompanyValue } from '@/types/company-values';
import type { EmployeeCompetency } from '@/types/manager-assessment/Competency';

import { LoaderIcon } from '@/components/OverlayLoader/OverlayLoader';
import { useAmelioTranslations } from '@/hooks';
import { groupCompetenciesByName } from '@/utils/competencyGrouping';
import { getLocalizedText } from '@/utils/localization';

interface EditCompetenciesModalProps {
    isOpen: boolean;
    competencies: EmployeeCompetency[];
    companyValues: CompanyValue[];
    initialSelectedIds: string[];
    initialIncludeCompanyValues: boolean;
    isSaving?: boolean;
    onClose: () => void;
    onSave: (result: { competencyIds: string[]; includeCompanyValues: boolean }) => void;
    dataTestId?: string;
}

export const EditCompetenciesModal = ({
    isOpen,
    competencies,
    companyValues,
    initialSelectedIds,
    initialIncludeCompanyValues,
    isSaving = false,
    onClose,
    onSave,
    dataTestId = 'edit-competencies-modal',
}: EditCompetenciesModalProps) => {
    const { t } = useAmelioTranslations();

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [includeCompanyValues, setIncludeCompanyValues] = useState(false);
    const [isCompanyValuesExpanded, setIsCompanyValuesExpanded] = useState(false);
    const [search, setSearch] = useState('');

    // Re-seed the local selection from the persisted state each time the modal opens.
    useEffect(() => {
        if (isOpen) {
            setSelectedIds(new Set(initialSelectedIds));
            setIncludeCompanyValues(initialIncludeCompanyValues);
            setIsCompanyValuesExpanded(initialIncludeCompanyValues);
            setSearch('');
        }
    }, [isOpen, initialSelectedIds, initialIncludeCompanyValues]);

    const activeCompanyValues = useMemo(
        () => companyValues.filter((value) => value.status === 'Active'),
        [companyValues],
    );

    const filteredGroups = useMemo(() => {
        const query = search.trim().toLowerCase();
        const matches = query
            ? competencies.filter((competency) => competency.name.toLowerCase().includes(query))
            : competencies;
        return groupCompetenciesByName(matches);
    }, [competencies, search]);

    const totalCount = competencies.length;
    const selectedCount = selectedIds.size;
    const allSelected = totalCount > 0 && selectedCount === totalCount;

    const toggleCompetency = (id: string) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleAll = () => {
        setSelectedIds(allSelected ? new Set() : new Set(competencies.map((c) => c.id)));
    };

    // Dirty only when the selection or the company-values toggle differs from what was loaded, so an
    // unchanged modal can't trigger a (regenerating) save.
    const isDirty =
        includeCompanyValues !== initialIncludeCompanyValues ||
        selectedIds.size !== initialSelectedIds.length ||
        initialSelectedIds.some((id) => !selectedIds.has(id));

    const canSave = isDirty && !isSaving;

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('managerAssessmentEvaluation.editCompetencies.title')}
            subtitle={t('managerAssessmentEvaluation.editCompetencies.subtitle')}
            size={'sm'}
            closeAriaLabel={t('common.close')}
            backdropBlur
            dataTestId={dataTestId}
            headerExtra={
                isSaving ? undefined : (
                    <div className={'flex flex-col gap-2'}>
                        <SearchBar
                            value={search}
                            onChange={setSearch}
                            placeholder={t(
                                'managerAssessmentEvaluation.editCompetencies.searchPlaceholder',
                            )}
                            ariaLabel={t(
                                'managerAssessmentEvaluation.editCompetencies.searchPlaceholder',
                            )}
                            dataTestId={'edit-competencies-search'}
                        />

                        <ButtonSecondary
                            size={'sm'}
                            onClick={toggleAll}
                            dataTestId={'edit-competencies-toggle-all'}
                        >
                            {allSelected
                                ? t('managerAssessmentEvaluation.editCompetencies.deselectAll')
                                : t('managerAssessmentEvaluation.editCompetencies.selectAll')}
                        </ButtonSecondary>
                    </div>
                )
            }
            footer={
                isSaving ? undefined : (
                    <div className={'flex items-center justify-between gap-3'}>
                        <Text
                            size={'small'}
                            color={Colors.BASE_500}
                        >
                            {t('managerAssessmentEvaluation.editCompetencies.selectedCount', {
                                selected: selectedCount,
                                total: totalCount,
                            })}
                        </Text>

                        <div className={'flex gap-3'}>
                            <ButtonSecondary
                                size={'sm'}
                                onClick={onClose}
                                dataTestId={'edit-competencies-cancel'}
                            >
                                {t('managerAssessmentEvaluation.editCompetencies.cancel')}
                            </ButtonSecondary>

                            <ButtonGradientPrimary
                                size={'sm'}
                                disabled={!canSave}
                                onClick={() =>
                                    onSave({
                                        competencyIds: [...selectedIds],
                                        includeCompanyValues,
                                    })
                                }
                                dataTestId={'edit-competencies-save'}
                            >
                                {t('managerAssessmentEvaluation.editCompetencies.save', {
                                    count: selectedCount,
                                })}
                            </ButtonGradientPrimary>
                        </div>
                    </div>
                )
            }
        >
            {isSaving ? (
                <div
                    className={'flex items-center justify-center py-12'}
                    data-testid={'edit-competencies-loader'}
                >
                    <LoaderIcon />
                </div>
            ) : (
                <div className={'flex flex-col gap-4'}>
                    {filteredGroups.length === 0 ? (
                        <Text
                            align={'center'}
                            color={Colors.BASE_500}
                            className={'py-8'}
                        >
                            {t('managerAssessmentEvaluation.editCompetencies.empty')}
                        </Text>
                    ) : (
                        filteredGroups.map((group) => (
                            <section
                                key={group.groupName || 'ungrouped'}
                                className={'flex flex-col gap-3'}
                            >
                                {group.groupName && (
                                    <Text
                                        size={'small'}
                                        weight={'semibold'}
                                        transform={'uppercase'}
                                        color={Colors.BASE_500}
                                    >
                                        {group.groupName}
                                    </Text>
                                )}

                                {group.competencies.map((competency) => (
                                    <Checkbox
                                        key={competency.id}
                                        checked={selectedIds.has(competency.id)}
                                        onChange={() => toggleCompetency(competency.id)}
                                        label={competency.name}
                                        dataTestId={`edit-competency-${competency.id}`}
                                    />
                                ))}
                            </section>
                        ))
                    )}

                    <CollapsibleSection
                        isOpen={isCompanyValuesExpanded}
                        onToggle={() => setIsCompanyValuesExpanded((value) => !value)}
                        className={'border-base-100 rounded-lg border'}
                        header={
                            <span className={'flex items-center gap-3'}>
                                <Icon
                                    name={'support-heart'}
                                    size={18}
                                    color={Colors.SECONDARY}
                                    ariaHidden
                                />

                                <Text
                                    as={'span'}
                                    weight={'medium'}
                                >
                                    {t(
                                        'managerAssessmentEvaluation.editCompetencies.companyValues',
                                    )}
                                </Text>
                            </span>
                        }
                        dataTestId={'edit-competencies-company-values'}
                    >
                        <div className={'flex flex-col gap-3 p-4'}>
                            <Text
                                size={'small'}
                                color={Colors.BASE_500}
                            >
                                {t(
                                    'managerAssessmentEvaluation.editCompetencies.companyValuesDescription',
                                )}
                            </Text>

                            <Checkbox
                                checked={includeCompanyValues}
                                onChange={setIncludeCompanyValues}
                                label={t(
                                    'managerAssessmentEvaluation.editCompetencies.includeCompanyValuesQuestions',
                                )}
                                dataTestId={'edit-competencies-include-company-values'}
                            />

                            {activeCompanyValues.map((value) => (
                                <div
                                    key={value.id}
                                    className={'border-base-100 bg-base-25 rounded-xl border p-3'}
                                    data-testid={`company-value-${value.id}`}
                                >
                                    <Text
                                        size={'small'}
                                        weight={'semibold'}
                                        color={Colors.BASE_600}
                                    >
                                        {getLocalizedText(value.name)}
                                    </Text>

                                    {value.description && (
                                        <Text
                                            size={'small'}
                                            color={Colors.BASE_500}
                                        >
                                            {getLocalizedText(value.description)}
                                        </Text>
                                    )}
                                </div>
                            ))}
                        </div>
                    </CollapsibleSection>
                </div>
            )}
        </ModalDialog>
    );
};
